<!--
  SupportComposer.svelte — writes a reply, with attachments.

  One composer for both sides of the desk. The customer form is a text box, a
  file picker and a send button. The agent form adds three things the customer
  must never see: a Reply / Internal note switch (the whole composer turns amber
  in note mode, matching how notes render in the thread, so which one is about
  to be sent is never in doubt), a template picker that fills placeholders
  in as it inserts — so the agent sees, and can still edit, exactly what goes
  out — and a status to set in the same call, which is how "reply and resolve"
  is one action rather than two that could half-succeed.

  When replying is not allowed the composer still renders, disabled, with the
  reason above it: a thread that simply ends reads as a bug, a closed door with
  a sign on it reads as a decision.
-->
<script lang="ts" module>
	import type { SupportTicketStatus } from '../AppTypes';

	/** What the composer hands its owner on send. */
	export interface SupportComposerMessage {
		/** The text, trimmed. */
		body: string;
		/** Files to attach. */
		files: File[];
		/** Whether it is an internal note (agents only). */
		isInternal: boolean;
		/** Status to set in the same call, when the agent chose one. */
		status?: SupportTicketStatus;
	}
</script>

<script lang="ts">
	import { Button, Spinner, Textarea } from 'flowbite-svelte';
	import SelectField from './store/SelectField.svelte';
	import type { SupportReplyTemplate } from '../AppTypes';
	import SupportAttachmentPicker from './SupportAttachmentPicker.svelte';
	import SupportMarkdown from './SupportMarkdown.svelte';
	import SupportUploadProgress from './SupportUploadProgress.svelte';
	import {
		substituteReplyTemplate,
		supportStatusDescription,
		supportStatusLabel,
		type ReplyTemplateContext
	} from '../support/supportFormat';
	import { t } from '../i18n';

	interface Props {
		/** Which side of the desk is writing. */
		mode?: 'customer' | 'agent';
		/** Whether replying is blocked. */
		disabled?: boolean;
		/** Why it is blocked, shown above the disabled form. */
		disabledReason?: string;
		/** Whether a send is in flight. */
		sending?: boolean;
		/** Fraction of the attachments uploaded so far, 0–1, while a send with files is in flight; `null` otherwise. */
		uploadProgress?: number | null;
		/** Reply templates offered to an agent. */
		replyTemplates?: SupportReplyTemplate[];
		/** What a reply template's placeholders are filled from. */
		templateContext?: ReplyTemplateContext | null;
		/**
		 * Sends the message. Resolve `true` to clear the composer — `false` (a
		 * failed send) keeps what was written, so nothing typed is lost.
		 */
		onsend: (_message: SupportComposerMessage) => Promise<boolean>;
	}

	let {
		mode = 'customer',
		disabled = false,
		disabledReason = '',
		sending = false,
		uploadProgress = null,
		replyTemplates = [],
		templateContext = null,
		onsend
	}: Props = $props();

	/** The text being written. */
	let body = $state('');
	/** Whether the preview is showing instead of the editor. */
	let previewing = $state(false);
	/** Files picked for attachment. */
	let files: File[] = $state([]);
	/** Whether an agent is writing an internal note rather than a reply. */
	let isInternal = $state(false);
	/** Status an agent chose to set with the send; empty leaves it to the server. */
	let sendStatus = $state('');
	/**
	 * Bumped after each template insert to remount the picker, which puts it back
	 * on its prompt: the dropdown keeps the row it last committed, so a value
	 * passed back down unchanged would not reset it.
	 */
	let templatePickerKey = $state(0);

	/** Statuses an agent may set with a message — never back to `new`. */
	let statusItems = $derived([
		{
			value: '',
			name: $t('Status: automatic'),
			description: $t('A reply hands the ticket to the customer; a note leaves the status as it is.')
		},
		...(['awaiting_agent', 'awaiting_customer', 'resolved', 'closed'] as SupportTicketStatus[]).map((status) => ({
			value: status,
			name: $t('Status: {{status}}', { status: $t(supportStatusLabel(status, 'agent')) }),
			description: $t(supportStatusDescription(status))
		}))
	]);

	/** Reply templates as picker items. */
	let templateItems = $derived([
		...replyTemplates.map((reply) => ({
			value: reply.templateUUID,
			name: reply.title,
			description: templateExcerpt(reply.body)
		}))
	]);

	/**
	 * The second line under a template in the picker: its opening words, on one line, so
	 * two templates with similar titles can be told apart before one is inserted.
	 */
	function templateExcerpt(text: string): string {
		const flat = text.replace(/\s+/g, ' ').trim();
		return flat.length > 90 ? `${flat.slice(0, 89).trimEnd()}…` : flat;
	}

	/** Whether there is anything to send. */
	let canSend = $derived(!disabled && !sending && body.trim().length > 0);



	/**
	 * Inserts a reply template with its placeholders filled in.
	 *
	 * Appended rather than replacing, so a reply the agent had started survives.
	 * The picker is reset afterwards, so the same reply can be inserted twice.
	 */
	function insertTemplate({ value }: { value: string }): void {
		const reply = replyTemplates.find((r) => r.templateUUID === value);
		templatePickerKey += 1;
		/* The picker only offers listed templates; a list that changed under it has nothing to insert. */
		if (!reply) return;
		const text = templateContext ? substituteReplyTemplate(reply.body, templateContext) : reply.body;
		body = body.trim() ? `${body.trimEnd()}\n\n${text}` : text;
	}

	/** Sends the message, clearing the composer only once the send succeeded. */
	async function send(): Promise<void> {
		/* Nothing to send, or a send already running. */
		if (!canSend) return;
		const sent = await onsend({
			body: body.trim(),
			files,
			isInternal: mode === 'agent' && isInternal,
			status: mode === 'agent' && sendStatus ? (sendStatus as SupportTicketStatus) : undefined
		});
		/* Keep the text on failure, so nothing typed is lost. */
		if (sent) {
			body = '';
			files = [];
			sendStatus = '';
			previewing = false;
		}
	}

	/** Ctrl/Cmd+Enter sends, as it does in every mail client. */
	function handleKeydown(event: KeyboardEvent): void {
		if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
			event.preventDefault();
			send();
		}
	}
</script>

<div class="space-y-3">
	{#if disabled && disabledReason}
		<div class="text-default flex items-start gap-2 rounded-lg bg-gray-100 p-3 text-sm dark:bg-gray-800" role="note">
			<i class="fa-solid fa-circle-info mt-0.5"></i>
			<span>{disabledReason}</span>
		</div>
	{/if}

	<div
		class="rounded-2xl p-3 transition-colors {mode === 'agent' && isInternal
			? 'bg-amber-50 dark:bg-amber-900/20'
			: 'bg-depth'}"
		data-note-mode={mode === 'agent' && isInternal ? 'true' : 'false'}
	>
		{#if mode === 'agent'}
			<div class="mb-2 flex flex-wrap items-center gap-2">
				<div class="inline-flex rounded-lg bg-gray-100 p-0.5 text-sm dark:bg-gray-800" role="group">
					<button
						type="button"
						class="rounded-md px-3 py-1 font-medium {!isInternal
							? 'bg-white shadow-sm dark:bg-gray-600'
							: 'text-gray-600 dark:text-gray-300'}"
						aria-pressed={!isInternal}
						onclick={() => (isInternal = false)}
					>
						<i class="fa-solid fa-reply me-1"></i>{$t('Reply')}
					</button>
					<button
						type="button"
						class="rounded-md px-3 py-1 font-medium {isInternal
							? 'bg-amber-200 text-amber-900 shadow-sm dark:bg-amber-500/30 dark:text-amber-100'
							: 'text-gray-600 dark:text-gray-300'}"
						aria-pressed={isInternal}
						onclick={() => (isInternal = true)}
					>
						<i class="fa-solid fa-lock me-1"></i>{$t('Internal note')}
					</button>
				</div>
				{#if replyTemplates.length > 0}
					{#key templatePickerKey}
					<SelectField
						class="ms-auto w-64"
						size="sm"
						placeholder={$t('Insert template…')}
						items={templateItems}
						value=""
						onchange={insertTemplate}
						aria-label={$t('Insert template…')}
						{disabled}
					/>
					{/key}
				{/if}
			</div>
		{/if}

		<!-- Write / Preview: replies are Markdown, and the preview renders exactly as
		     the thread and the customer's email will. -->
		<div class="mb-2 flex items-center gap-3 text-xs">
			<div class="inline-flex rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800" role="group" aria-label={$t('Editor mode')}>
				<button
					type="button"
					class="rounded-md px-2.5 py-0.5 font-medium {!previewing
						? 'bg-white shadow-sm dark:bg-gray-600'
						: 'text-gray-600 dark:text-gray-300'}"
					aria-pressed={!previewing}
					onclick={() => (previewing = false)}
				>
					{$t('Write')}
				</button>
				<button
					type="button"
					class="rounded-md px-2.5 py-0.5 font-medium {previewing
						? 'bg-white shadow-sm dark:bg-gray-600'
						: 'text-gray-600 dark:text-gray-300'}"
					aria-pressed={previewing}
					onclick={() => (previewing = true)}
				>
					{$t('Preview')}
				</button>
			</div>
			<span class="text-default ms-auto">
				<i class="fa-brands fa-markdown me-1"></i>{$t('Markdown supported')}
			</span>
		</div>

		{#if previewing}
			<div class="min-h-[8.5rem] rounded-lg border border-gray-200 p-3 dark:border-gray-600" data-testid="composer-preview">
				{#if body.trim()}
					<SupportMarkdown text={body} class="text-sm leading-relaxed" />
				{:else}
					<p class="text-default text-sm italic">{$t('Nothing to preview yet.')}</p>
				{/if}
			</div>
		{:else}
		<Textarea
			rows={5}
			divClass="!w-full"
			class="!w-full"
			bind:value={body}
			{disabled}
			placeholder={mode === 'agent' && isInternal
				? $t('Write a note only the support team can see…')
				: $t('Write your reply…')}
			aria-label={mode === 'agent' && isInternal ? $t('Internal note') : $t('Reply')}
			onkeydown={handleKeydown}
		/>
		{/if}

		<div class="mt-2">
			<SupportAttachmentPicker bind:files compact {disabled} id="composer-files" testid="composer-files" />
			<SupportUploadProgress progress={uploadProgress} />
		</div>

		<div class="mt-3 flex flex-wrap items-center gap-2">
			<div class="grow"></div>
			{#if mode === 'agent'}
				<SelectField
					class="w-56"
					size="sm"
					items={statusItems}
					bind:value={sendStatus}
					aria-label={$t('Status after sending')}
					{disabled}
				/>
			{/if}
			<Button size="sm" disabled={!canSend} onclick={send}>
				{#if sending}<Spinner size="4" class="me-2" />{:else}<i class="fa-solid fa-paper-plane me-2"></i>{/if}
				{mode === 'agent' && isInternal ? $t('Add note') : $t('Send')}
			</Button>
		</div>
	</div>
</div>
